import { useCallback } from "react";
import BackgroundImage from "../../../components/ui/BackgroundImage";
import Text from "../../../components/ui/Text";
import carImg from "../assets/images/car-bg.webp";
import cardBg from "../assets/images/card-bg.webp";
import FloatingCard, {
  FloatingCardBody,
} from "../../../components/ui/animated/FloatingCard";

import { motion, AnimatePresence } from "framer-motion";

interface CardData {
  firstName: string;
  lastName: string;
  birthday: string;
  gender: "men" | "women";
  expiresAt: string;
  nationality: string;
  licenseId: string;
  profilePhoto: string;
  signaturePhoto: string;
}

interface Props {
  className?: string;
  data: CardData;
  title: string;
  footerTitle?: string;
  blur?: (keyof CardData)[];
  renderOptions?: {
    fieldOrderRows: (keyof CardData)[][];
    includeCloseButton?: boolean;
  };
}

const labels: Partial<Record<keyof CardData, string>> = {
  firstName: "Civilian Name",
  lastName: "Civilian Last Name",
  birthday: "Birthday",
  gender: "Gender",
  expiresAt: "Expir. Date",
  nationality: "Nationality",
  licenseId: "Document License ID",
  profilePhoto: "Profile Photo",
  signaturePhoto: "Signature",
};

export default function Card({
  className = "",
  data,
  blur,
  title,
  footerTitle = "Atasat Diplomatic",
  renderOptions = {
    fieldOrderRows: [
      ["firstName"],
      ["lastName"],
      ["birthday", "gender", "expiresAt"],
      ["nationality"],
    ],
    includeCloseButton: true,
  },
}: Props) {
  const isBlurred = useCallback(
    (key: keyof CardData) => blur?.includes(key),
    [blur]
  );

  const Field = ({ field }: { field: keyof CardData }) => (
    <div className="flex flex-col max-w-fit">
      <Text variant="span" className="text-black/50 " size="xxs">
        {labels[field] ?? field}
      </Text>
      <Text
        size="sm"
        className={`text-[#2E2E2E] font-semibold ${
          isBlurred(field) ? "blur-sm bg-black/40 select-none" : ""
        }`}
      >
        <span className={isBlurred(field) ? "opacity-0" : ""}>
          {data[field]}
        </span>
      </Text>
    </div>
  );

  return (
    <BackgroundImage
      className={`flex items-center justify-center h-screen  ${className}`}
      imageSrc={carImg}
    >
      <div className="nekst-font">
        <FloatingCard>
          <FloatingCardBody>
            <AnimatePresence>
              <motion.div
                key="animated-box"
                initial={{ opacity: 0, y: 50 }} // start position (below)
                animate={{ opacity: 1, y: 0 }} // visible
                exit={{ opacity: 0, y: 50 }} // leave down
                transition={{ duration: 0.4, ease: "easeOut" }}
                className=""
              >
                <div className="bg-black/40 p-7 pt-[4.5rem] rounded-[16px] min-w-[620px]">
                  {/** Header */}
                  <div
                    style={{
                      backgroundImage:
                        "radial-gradient(ellipse at center, #FFD230 0%, #C3A124 70%)",
                    }}
                    className="h-[60px] rounded-t-[7px] flex items-center justify-center"
                  >
                    <Text className="text-white" size="lgx">
                      {title}
                    </Text>
                  </div>

                  {/** Main */}
                  <BackgroundImage imageSrc={cardBg} className="min-h-[340px]">
                    <div className="p-3 h-full">
                      {/** Profile photo & Information */}
                      <div className="flex items-center space-x-5">
                        <div className="overflow-hidden group">
                          <img
                            src={data.profilePhoto}
                            alt="Profile"
                            className="w-[140px] h-[180px] group-hover:scale-[120%] transition-all duration-500"
                          />
                        </div>
                        <div className="space-y-2">
                          {renderOptions.fieldOrderRows.map((row, i) => (
                            <div
                              key={i}
                              className="flex flex-wrap gap-10"
                              style={{
                                gridTemplateColumns: `repeat(${row.length}, 1fr)`,
                              }}
                            >
                              {row.map((field) => (
                                <Field key={field} field={field} />
                              ))}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/** Signature */}
                      <div className="border border-[#BABABA] p-3 pt-0 mt-3 flex items-center space-x-5">
                        <img
                          src={data.signaturePhoto}
                          alt="Signature"
                          className="w-[126px] h-[64px] bottom-[-20px] relative"
                        />
                        <Field field="licenseId" />
                      </div>

                      {/** Footer */}
                      <Text
                        className="text-[#41808B] text-center my-3"
                        size="lgx"
                      >
                        {footerTitle}
                      </Text>
                    </div>
                  </BackgroundImage>
                </div>
              </motion.div>
            </AnimatePresence>
          </FloatingCardBody>
        </FloatingCard>
      </div>
    </BackgroundImage>
  );
}
